import React, { useEffect, useState } from 'react';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { apiGet } from '../../utils/api';

/**
 * ReviewCard
 * Reusable review card used across pages (EscrowService, Profile reviews tab)
 * Props:
 * - review: { user, avatar, platform, product, rating, text, date, amount }
 */
const ReviewCard = ({ review }) => {
  const { isDarkMode } = useDarkMode();
  const { user, avatar, platform, product, rating = 5, text, date, amount } = review || {};
  const [resolvedAvatar, setResolvedAvatar] = useState(null);

  const isRealAvatar = (url) => {
    if (!url || typeof url !== 'string') return false;
    const u = url.trim();
    if (u.includes('ui-avatars.com') || u.includes('pravatar.cc')) return false;
    return u.startsWith('http') || u.startsWith('data:image/');
  };

  useEffect(() => {
    if (isRealAvatar(avatar) || isRealAvatar(resolvedAvatar)) return;
    const uname = String(user || '').trim();
    if (!uname) return;
    let aborted = false;
    (async () => {
      try {
        const res = await apiGet(`/api/users/public/${encodeURIComponent(uname)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (aborted) return;
        if (data && isRealAvatar(data.avatar)) {
          setResolvedAvatar(data.avatar);
        }
      } catch (_) {}
    })();
    return () => { aborted = true; };
  }, [user, avatar]);

  return (
    <div className={`rounded-2xl p-5 shadow-lg transition-colors duration-300 ${isDarkMode ? 'bg-[#1a1a1a] border border-gray-700' : 'bg-white border border-gray-200'}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="relative w-10 h-10">
          {isRealAvatar(resolvedAvatar || avatar) ? (
            <img src={resolvedAvatar || avatar} alt={user} className="w-10 h-10 rounded-full object-cover border" loading="lazy" />
          ) : (
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-gray-200 text-gray-700'}`}>{String(user || 'U').slice(0,2).toUpperCase()}</div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{user}</div>
          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{platform} • {date ? new Date(date).toLocaleDateString() : ''}</div>
        </div>
        <div className="flex items-center gap-0.5" aria-label={`Rating ${rating} out of 5`}>
          {[...Array(5)].map((_, idx) => (
            <svg key={idx} className={`w-4 h-4 ${idx < rating ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
          ))}
        </div>
      </div>
      <div className={`text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{text}</div>
      <div className="flex items-center justify-between mt-1">
        {product ? (
          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Product: <span className="font-semibold">
              {product.length > 50 ? `${product.substring(0, 50)}...` : product}
            </span>
          </div>
        ) : <span />}
        {typeof amount !== 'undefined' && amount !== null && (
          <div className={`text-xs font-bold px-2 py-1 rounded-full ${isDarkMode ? 'bg-[#0f0f0f] text-green-300 border border-green-900/40' : 'bg-green-50 text-green-700 border border-green-200'}`}>
            ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewCard;


